'use client';

import { useEffect } from 'react';
import { usePathname } from 'next/navigation';

/**
 * ScrollToTop - Scrolls to top of page on refresh
 * 
 * This component ensures that when a user refreshes the page,
 * they are taken to the top of the page for better UX.
 * Uses Lenis smooth scroll if available, otherwise falls back to native scroll.
 * 
 * Detects page refresh using Performance Navigation API.
 */
export default function ScrollToTop() {
  const pathname = usePathname();

  useEffect(() => {
    // Detect if this is a page refresh vs initial navigation
    const isRefresh = (() => {
      // Check Performance Navigation API (most reliable method)
      if (typeof window !== 'undefined' && 'performance' in window) {
        const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
        if (navigation) {
          // 'reload' means the page was refreshed
          return navigation.type === 'reload';
        }
      }
      
      // Fallback: Check if we've visited this specific path before in this session
      const storageKey = `scroll-to-top-${pathname}`;
      const wasHereBefore = sessionStorage.getItem(storageKey);
      
      if (wasHereBefore) {
        // We've been to this exact path before, so this is likely a refresh
        return true;
      }
      
      // Mark that we've visited this path
      sessionStorage.setItem(storageKey, 'true');
      return false;
    })();

    // Only scroll to top on refresh, not on initial navigation
    if (isRefresh) {
      const handleScrollToTop = () => {
        // Use Lenis if available (smooth scroll)
        const lenis = (window as unknown as { lenis?: { scrollTo: (target: number | string, options?: { immediate?: boolean }) => void } }).lenis;
        
        if (lenis) {
          // Use Lenis smooth scroll to top
          lenis.scrollTo(0, { immediate: false });
        } else {
          // Fallback to native scroll
          window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
        }
      };

      // Small delay to ensure page is fully loaded and Lenis is initialized
      const timeoutId = setTimeout(handleScrollToTop, 100);

      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, [pathname]);

  return null;
}

